/**
 * A pult nyomtató-jelzője (Karlo terve, 2026-09-16; valódi állapot: #178).
 *
 * TÖMÖR, mert a fejléc eszközsorában áll: nyomtató-ikon, a nyomtatóválasztó
 * (ha több nyomtató van), az állapot és egy fogaskerék-menü. A Tesztcímke, a
 * Kalibrációs címke és a Beállítás a menüben van — ezek a pult felállításához
 * kellenek, nem a beléptetés közbeni munkához.
 *
 * Két üzemmód, a {@link useLabelPrinter} választja:
 *
 * - **Zebra** — a szervezetnek van bekötött nyomtatója. A jelző a KIVÁLASZTOTT
 *   nyomtató valódi állapotát mutatja (kész / kifogyott a címke / nyitva a
 *   fedél / szünetel / offline), ahogy az ügynöke jelenti.
 * - **Böngésző** — nincs nyomtató bekötve. A jelző ilyenkor semleges: a
 *   `window.print()` nem tud a nyomtatóról semmit, és egy „kész" felirat olyat
 *   állítana, amit a program nem tud.
 */

import { PrinterOutlined, SettingOutlined } from '@ant-design/icons';
import { Badge, Button, Dropdown, Select, Space, Tag, Tooltip, Typography } from 'antd';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import type { PrintAgent, PrinterState } from '../config/labelPrinter';

const { Text } = Typography;

interface PrinterIndicatorProps {
  mode: 'zebra' | 'browser';
  state: PrinterState;
  queued: number;
  /** A szervezet nyomtatói; kettőnél ez a gép választ közülük. */
  agents: PrintAgent[];
  selectedId: string | null;
  onSelect: (agentId: string) => void;
  onTestLabel: () => void;
  onCalibrationLabel: () => void;
  onSetup: () => void;
  setupDisabled?: boolean;
}

function badgeStatus(state: PrinterState): 'success' | 'error' | 'warning' | 'default' {
  if (state === 'ready') return 'success';
  if (state === 'none') return 'default';
  if (state === 'paused' || state === 'unselected') return 'warning';
  return 'error';
}

export function PrinterIndicator({
  mode,
  state,
  queued,
  agents,
  selectedId,
  onSelect,
  onTestLabel,
  onCalibrationLabel,
  onSetup,
  setupDisabled,
}: PrinterIndicatorProps): React.ReactElement {
  const { t } = useTranslation();
  const zebra = mode === 'zebra';
  const single = agents.length === 1 ? agents[0] : null;

  const menu = {
    items: [
      { key: 'test', label: t('checkin.testLabel') },
      // A kalibráció SAJÁT tétel, nem a Tesztcímke helyett: a kettő két
      // különböző kérdésre felel (milyen / mekkora).
      { key: 'calibration', label: t('checkin.calibrationLabel') },
      { type: 'divider' as const },
      { key: 'setup', label: t('checkin.printerSetup'), disabled: setupDisabled },
    ],
    onClick: ({ key }: { key: string }) => {
      if (key === 'test') onTestLabel();
      else if (key === 'calibration') onCalibrationLabel();
      else if (key === 'setup') onSetup();
    },
  };

  return (
    <Tag style={{ padding: '2px 4px 2px 10px', margin: 0 }}>
      <Space size={8}>
        <Tooltip title={t(zebra ? 'checkin.printerZebraTooltip' : 'checkin.printerTooltip')}>
          <PrinterOutlined aria-label={t('checkin.printerIndicator')} />
        </Tooltip>
        {/*
          Több nyomtatónál ez a gép választ (#178): a QR-pult gépei az egyikre,
          a kézi pult gépei a másikra nyomtatnak. Egynél a neve áll itt.
        */}
        {agents.length > 1 ? (
          <Select
            size="small"
            variant="borderless"
            value={selectedId && agents.some((a) => a.id === selectedId) ? selectedId : undefined}
            placeholder={t('checkin.printerSelect')}
            onChange={onSelect}
            options={agents.map((a) => ({ value: a.id, label: a.name }))}
            style={{ minWidth: 140 }}
            aria-label={t('checkin.printerSelect')}
            popupMatchSelectWidth={false}
          />
        ) : (
          <Text>{single ? single.name : t('checkin.printerIndicator')}</Text>
        )}
        {state === 'none' ? (
          <Text type="secondary">{t('checkin.printerFromBrowser')}</Text>
        ) : (
          <Badge
            status={badgeStatus(state)}
            text={
              <Text type={state === 'ready' ? undefined : 'danger'}>
                {t(`checkin.printerState.${state}`)}
              </Text>
            }
          />
        )}
        {zebra && queued > 0 && (
          <Text type="secondary">{t('checkin.printerQueued', { count: queued })}</Text>
        )}
        <Dropdown menu={menu} trigger={['click']} placement="bottomRight">
          <Button
            type="text"
            size="small"
            icon={<SettingOutlined />}
            aria-label={t('checkin.printerMenu')}
          />
        </Dropdown>
      </Space>
    </Tag>
  );
}
